import { ILogger } from "@tdp/common/telemetry";
import { IAppValidationReader } from "../abstractions/IAppValidationReader";
import { createCommands } from "@tdp/common/commandHandler";
import { ApiFetchStatus } from "@tdp/common/constants";
import { appValidationSlice } from "./slice";
import { notificationSlice, NotificationType } from '@tdp/common/notification';
import { IAppValidationWriter } from "../abstractions/IAppValidationWriter";
import { IAppValidationNewRequest } from "../abstractions/IAppValidationCreate";
import { appValidationExportUtils } from "@tdp/common/downloadUtils/downloadStandaloneFiles";
import downloadFile from "@tdp/common/downloadUtils/downloadFile";

export interface IAppValidationCommandHandler {
  logger: ILogger;
  appValidation: IAppValidationReader;
  appValidationWriter: IAppValidationWriter
}

export const createAppValidationCommandHandler = ({ logger, appValidation, appValidationWriter }: IAppValidationCommandHandler) =>
  createCommands(
    {
      name: "appValidationCommands",
      handlers: {
        async *reset() {
          yield appValidationSlice.actions.reset();
          return;
        },

        async *resetCurrentValidation() {
          yield appValidationSlice.actions.resetCurrentValidation();
          return;
        },

        async *resetNewValidationId() {
          yield appValidationSlice.actions.resetNewValidationId();
          return;
        },

        async *setValidationAppId(appId: string) {
          yield appValidationSlice.actions.setValidationAppId(appId);
          return;
        },

        async *getCount(appId: string) {
          const response = await appValidation.getCount(appId);
          if (response.type === ApiFetchStatus.SUCCESS) {
            yield appValidationSlice.actions.validationCountLoaded(response.response);
            return;
          }
        },

        async *getValidationDetails({ validationId }: { validationId: string }) {
          const response = await appValidation.getValidationDetails(validationId);
          if (response.type === ApiFetchStatus.SUCCESS && response.response) {
            yield appValidationSlice.actions.getValidationDetailListSuccess(response.response);
            return;
          }
          if (response.type === ApiFetchStatus.ERROR) {
            yield notificationSlice.actions.showNotification({
              message: response.errorMessage ?? `Unable to fetch validation detials`,
              show: true,
              autoDismiss: true,
              type: NotificationType.Error,
            });
            return;
          }
        },

        async *getValidationsList(
          { appId, pageSize, continuationToken }: { appId: string, pageSize?: number, continuationToken?: string }
        ) {
          yield appValidationSlice.actions.getValidationListStart();
          const response = await appValidation.getValidationsList(appId, pageSize, continuationToken);
          if (response.type === ApiFetchStatus.SUCCESS && response.response) {
            yield appValidationSlice.actions.getValidationListSuccess(response.response);
            return;
          }
          if (response.type === ApiFetchStatus.ERROR) {
            yield notificationSlice.actions.showNotification({
              message: response.errorMessage ?? `Unable to fetch validation list`,
              show: true,
              autoDismiss: true,
              type: NotificationType.Error,
            });
            return;
          }
        },

        async *resetPreValidation() {
          yield appValidationSlice.actions.resetPreValidation();
          return;
        },

        async *preValidateAppPackage({ appPackage }: { appPackage: File }) {
          const response = await appValidation.preValidateAppPackage(appPackage);
          if (response.type === ApiFetchStatus.SUCCESS && response.response) {
            yield appValidationSlice.actions.setPreValidation(response.response)
            return;
          } else if (response.type === ApiFetchStatus.ERROR) {
            yield notificationSlice.actions.showNotification({
              message: `Failed to validate app package`,
              show: true,
              autoDismiss: true,
              type: NotificationType.Error,
            });
            return;
          }
        },

        async *preValidateAppDefinition({ appId }: { appId: string }) {
          const response = await appValidation.preValidateAppDefinition(appId);
          if (response.type === ApiFetchStatus.SUCCESS && response.response) {
            yield appValidationSlice.actions.setPreValidation(response.response)
            return;
          } else if (response.type === ApiFetchStatus.ERROR) {
            yield notificationSlice.actions.showNotification({
              message: `Failed to validate app definition`,
              show: true,
              autoDismiss: true,
              type: NotificationType.Error,
            });
            return;
          }
        },

        async *createNewValidation(validation: IAppValidationNewRequest) {
          const response = await appValidationWriter.newRequest(validation);
          if (response.type === ApiFetchStatus.SUCCESS && response.response) {
            yield appValidationSlice.actions.setNewValidationId(response.response.appValidationId || "");
            return;
          } else if (response.type === ApiFetchStatus.ERROR) {
            yield appValidationSlice.actions.setNewValidationId("FAILED");
            yield notificationSlice.actions.showNotification({
              message: response.errorMessage ?? `App validation request failed`,
              show: true,
              autoDismiss: true,
              type: NotificationType.Error,
            });
            return;
          }
        },

        async *exportDetails({ validationId, ext }: { validationId: string, ext: 'csv' | 'zip' }) {
          const response = await appValidation.exportDetails(validationId, ext);
          if (response.type === ApiFetchStatus.SUCCESS && response.response) {
            if (ext === 'zip') {
              downloadFile(validationId, response.response);
            }
            else {
              appValidationExportUtils(response.response, validationId, 'csv');
            }
            return;
          }
          if (response.type === ApiFetchStatus.ERROR) {
            yield notificationSlice.actions.showNotification({
              message: response.errorMessage ?? `Unable to download file`,
              show: true,
              autoDismiss: true,
              type: NotificationType.Error,
            });
            return;
          }
        }
      }
    });
